import DatePicker, { registerLocale } from "react-datepicker";
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";

import {
	Box,
	Button,
	Checkbox,
	CheckboxGroup,
	Flex,
	HStack,
	Popover,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	Stack,
	Text,
	useToast,
	VStack,
} from "@chakra-ui/react";
import sv from "date-fns/locale/sv";
import moment from "moment";
import { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import styled from "styled-components";
import DropDownIcon from "../components/icons/DropDownIcon";
import { LoggedInLayout } from "../components/layouts/LoggedInLayout";
import LoadingIndicator from "../components/Loading";
import ToggleSwitch from "../components/ToggleSwitch";
import { useCustomerUsersCustomers, useJobStats } from "@api/hooks";
import { apiGet } from "../api/client";
import {
	getFirstDateOfYear,
	getISOWeeksList,
	getLastDateOfYear,
	getMonthNumbersList,
	getShortSwedishMonthName,
} from "@utils/dateUtils";

const chartTypes = [
	{ key: "hours", name: "Bokade timmar" },
	{ key: "jobs", name: "Bokade uppdrag" },
];
const chartGroups = [
	{ key: "month", name: "Månad" },
	{ key: "week", name: "Vecka" },
];

const StatisticsPage = () => {
	registerLocale("sv", sv);
	const { customers, isLoading: isLoadingCustomerList } = useCustomerUsersCustomers();

	const toast = useToast();
	const [selectedUnits, setSelectedUnits] = useState<string[]>(customers?.customers.map((c) => c.id) ?? []);
	const [chartType, setChartType] = useState<string>(chartTypes[0].key);
	const [chartGroup, setChartGroup] = useState<string>(chartGroups[0].key);
	const [dateRange, setDateRange] = useState<Date[]>([getFirstDateOfYear(new Date()), getLastDateOfYear(new Date())]);
	const [startDate, endDate] = dateRange;

	const { stats, isLoading: isLoadingChartData } = useJobStats(
		moment(startDate).toISOString(),
		moment(endDate).toISOString(),
		selectedUnits ? selectedUnits : [],
		chartGroup ? chartGroup : "",
		chartType ? chartType : ""
	);

	useEffect(() => {
		setSelectedUnits(customers?.customers.map((c) => c.id) ?? []);
	}, [JSON.stringify(customers?.customers.map((c) => c.id))]);

	const downloadReport = async () => {
		try {
			const data = await apiGet<{ fileName: string; url: string }>("/report/customer-job-stats", {
				startDate: moment(startDate).format(),
				endDate: moment(endDate).format(),
				customerIds: selectedUnits ? selectedUnits.join(",") : "",
			});

			if (data.fileName && data.url) {
				const downloadLink = document.createElement("a");
				downloadLink.href = "/origo/api" + data.url;
				downloadLink.download = "/origo/api" + data.url;
				downloadLink.click();
			}
		} catch (error) {
			console.error({ "Nedladdningen misslyckades": error });
			toast({
				title: "Nedladdningen misslyckades",
				status: "error",
				duration: 5000,
				isClosable: true,
				variant: "subtle",
			});
		}
	};

	const customTooltip = (active: boolean, payload: any[], label: string) => {
		if (active && payload && payload.length) {
			return (
				<Box bg={"#FFFFFF"} borderRadius={"3px"} border={"1px solid #B5B4B2"} w="fit-content" p={1}>
					<Text fontSize={"14px"} fontWeight={600} color={"#4E7EA7"} m={0}>
						{label}
					</Text>
					<Text fontSize={"12px"} fontWeight={600} m={0}>{`${
						chartType === chartTypes[0].key ? "Timmar" : "Uppdrag"
					} : ${payload[0].value.toString().replace(".", ",")}`}</Text>
				</Box>
			);
		}

		return null;
	};

	const getChartStats = (): any[] => {
		if (chartGroup === chartGroups[0].key) {
			// month
			const monthList = getMonthNumbersList(startDate, endDate);
			if (stats) {
				if (stats?.length === monthList.length)
					return stats?.map((item) => ({ ...item, name: item.month ? getShortSwedishMonthName(item.month) : "" }));
				let statData = [];
				for (let m = 1; m <= monthList.length; m++) {
					statData.push({
						name: getShortSwedishMonthName(monthList[m - 1].month),
						value:
							stats.find((stat) => stat.month === monthList[m - 1].month && stat.year === monthList[m - 1].year)
								?.value ?? 0,
					});
				}
				return statData;
			}
			return [];
		} else {
			const weekList = getISOWeeksList(startDate, endDate);
			if (stats) {
				if (stats.length === weekList.length)
					return stats.map((item) => ({ ...item, name: item.week ? item.week : "" }));
				let statData = [];
				for (let w = 1; w <= weekList.length; w++) {
					statData.push({
						name: weekList[w - 1].week,
						value:
							stats.find((stat) => stat.week === weekList[w - 1].week && stat.year === weekList[w - 1].year)?.value ??
							0,
					});
				}
				return statData;
			}
			return [];
		}
	};

	const hasChartData = stats && stats.length > 0 && getChartStats()?.length > 0;

	return (
		<LoggedInLayout width="l" bg={"#edeff3"}>
			<VStack spacing={"1.5rem"} align={"flex-start"} w="full">
				<Text fontWeight={600} fontSize={24} mt={2} mb={0}>
					Statistik
				</Text>
				<Box borderRadius={"lg"} w={"100%"} backgroundColor={"white"} p={7} mt={3} mb={3}>
					{isLoadingCustomerList && <LoadingIndicator size={"s"} />}
					{!isLoadingCustomerList && (
						<VStack>
							<HStack w={"100%"} justify={"space-between"} mb={6}>
								<Popover placement="bottom-start">
									<PopoverTrigger>
										<Flex
											height={"44px"}
											width={"280px"}
											borderColor={"#B5B4B2"}
											borderWidth={1}
											borderRadius={14}
											_hover={{ cursor: "pointer" }}
											direction={"row"}
											alignItems={"center"}
											justify={"space-between"}
											backgroundColor={"#FFFFFF"}
											p={2}
										>
											<Text m={0} fontSize="16px" fontWeight={400}>
												Filtrera på enhet
											</Text>

											<DropDownIcon />
										</Flex>
									</PopoverTrigger>
									<PopoverContent>
										<PopoverBody>
											<CheckboxGroup
												colorScheme="blue"
												value={selectedUnits}
												onChange={(value) => {
													setSelectedUnits(value as string[]);
												}}
											>
												<Stack spacing={2} direction={"column"}>
													{customers?.customers.map((item) => (
														<Checkbox key={item.id} value={item.id}>
															{item.name ?? ""}
														</Checkbox>
													))}
												</Stack>
											</CheckboxGroup>
										</PopoverBody>
									</PopoverContent>
								</Popover>
								<ToggleSwitch
									onChange={(key) => setChartType(key)}
									optionData={chartTypes}
									value={chartType}
									label="Visa"
								/>
								<ToggleSwitch
									onChange={(key) => setChartGroup(key)}
									optionData={chartGroups}
									value={chartGroup}
									label="Per"
								/>
							</HStack>
							<StyledWrapper>
								<DatePicker // setting this up locale wise
									selectsRange={true}
									startDate={startDate}
									endDate={endDate}
									onChange={(update) => {
										// @ts-ignore
										setDateRange(update);
									}}
									className="date-picker"
									showWeekNumbers
									dateFormat="yyyy/MM/dd"
									locale={"sv"}
								/>
							</StyledWrapper>
							{isLoadingChartData && (
								<Box width={950} height={450}>
									<LoadingIndicator size={"s"} />
								</Box>
							)}
							{!isLoadingChartData && hasChartData && (
								<VStack>
									<HStack>
										<Text textAlign={"center"} fontWeight={400} fontSize={"14px"} m={0} transform="rotate(-90deg)">
											{chartType === chartTypes[0].key ? "Timmar" : "Uppdrag"}
										</Text>
										<BarChart
											style={{ marginTop: "15px", marginBottom: "15px" }}
											width={900}
											height={450}
											data={getChartStats()}
											margin={{
												top: 30,
												right: 10,
												left: 0,
												bottom: 0,
											}}
										>
											<CartesianGrid strokeDasharray="3 3" vertical={false} />
											<XAxis dataKey="name" />
											<YAxis tickFormatter={(value, index) => value.toFixed(1).toString().replace(".", ",")} />
											<Tooltip
												content={({ active, payload, label }) => customTooltip(!!active, payload ?? [], label)}
											/>
											<Bar dataKey="value" fill="#4E7EA7" maxBarSize={15} />
										</BarChart>
									</HStack>
									<Text textAlign={"center"} fontWeight={400} fontSize={"14px"} m={0} mb={8}>
										{chartGroup === chartGroups[0].key ? "Månad" : "Vecka"}
									</Text>
								</VStack>
							)}
							{!isLoadingChartData && (!stats || !hasChartData) && (
								<Box width={950} height={450}>
									<Text textAlign={"center"} fontWeight={600} fontSize={"14px"}>
										{"Inga data att visa i diagrammet"}
									</Text>
								</Box>
							)}
							<Button colorScheme="linkedin" variant="link" size="sm" onClick={downloadReport}>
								Ladda ner som excel-fil
							</Button>
						</VStack>
					)}
				</Box>
			</VStack>
		</LoggedInLayout>
	);
};

export default StatisticsPage;

const StyledWrapper = styled.div`
	.date-picker {
		width: 250px;
		text-align: center;
		border: 1px solid #b5b4b2;
		border-radius: 14px;
		padding: 8px;
		font-size: 16px;
	}
`;
